import { Button, Flex, Skeleton, useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import { useEffect, useState } from 'react';
import CenteredSpinner from 'src/components/CenterSpinner/CenteredSpinner';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { ConfirmationPasswordModal } from '@/components/ConfirmationPasswordModal';
import { ProfileImage } from '@/components/ProfileImage';
import { AccountDetailHeaderInfo } from './components/AccountDetailHeaderInfo';
import { AccountDetailSectionHeader } from './components/AccountDetailSectionHeader';
import { breadcrumb } from './breadcrumb';
import { AccountDetailTabs } from './components/AccountDetailTabs';
import { useFetchAccountById, usePutAccountAvatarMutation, usePutAccountDeleteMutation } from '@/services/queries/account';
import { useTranslation } from 'react-i18next';

export const AccountDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [isOpenDeleteAccountModal, setIsOpenDeleteAccountModal] = useState(false);
  const [isOpenConfirmationPasswordModal, setIsOpenConfirmationPasswordModal] = useState(false);

  const { data: item, isLoading, fetchAccountById } = useFetchAccountById();
  const { t } = useTranslation();

  const putAccountAvatarMutation = usePutAccountAvatarMutation();

  const deleteUserMutation = usePutAccountDeleteMutation();

  const updateProfileImage = (image: File) => {
    if (!item?.uuid) return;
    putAccountAvatarMutation.mutate(
      { id: item?.uuid, payload: { image } },
      {
        onSuccess: async () => {
          toast({
            status: 'success',
            title: t('pages.accountdetail.success_image'),
          });

          await queryClient.invalidateQueries(['GET_ACCOUNT_BY_ID', item?.uuid]);
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.accountdetail.error'),
          });
        },
      },
    );
  };

  const handleDeleteAccountConfirmation = () => {
    setIsOpenDeleteAccountModal(true);
  };

  const deleteAccount = () => {
    deleteUserMutation.mutate(id ?? '', {
      onSuccess: () => {
        toast({
          status: 'success',
          title: t('pages.accountdetail.delete_success'),
        });
        navigate('/account');
      },
    });
  };

  useEffect(() => {
    if (!id) {
      navigate('/');
    }
    fetchAccountById(id ?? '');
  }, [id, fetchAccountById, navigate]);

  return (
    <PageContent title={t('pages.accountdetail.title')} breadcrumbItems={breadcrumb}>
      <SectionBox>
        <Flex gap='xlarge' rowGap='xxxsmall' justifyContent='space-between' direction={{ base: 'column', md: 'row' }}>
          <Flex gap='xlarge' rowGap='xxxsmall' direction={{ base: 'column' }}>
            {item && <AccountDetailSectionHeader item={item} isLoading={isLoading} />}

            <Flex gap='xlarge' rowGap='xxxsmall' direction={{ base: 'column', md: 'row' }}>
              <ProfileImage
                src={item?.avatar_url}
                onImageChange={updateProfileImage}
                isLoading={putAccountAvatarMutation.isLoading}
              />

              {item && <AccountDetailHeaderInfo item={item} isLoading={isLoading} />}
            </Flex>
          </Flex>

          {isLoading ? (
            <Skeleton w='full' h='40px' maxWidth='560px' />
          ) : (
            <Button backgroundColor='red.500' _hover={{ backgroundColor: 'red.600' }} onClick={handleDeleteAccountConfirmation}>
              {t('pages.accountdetail.button_confirm')}
            </Button>
          )}
        </Flex>

        {item && <AccountDetailTabs isLoading={isLoading} item={item} />}
      </SectionBox>

      <ConfirmationModal
        isOpen={isOpenDeleteAccountModal}
        onClose={() => setIsOpenDeleteAccountModal(false)}
        onConfirm={() => {
          setIsOpenDeleteAccountModal(false);
          setIsOpenConfirmationPasswordModal(true);
        }}
        isLoading={false}
        title={t('pages.accountdetail.modal_title')}
        description={t('pages.accountdetail.modal_description')}
        confirmLabel={t('pages.accountdetail.button_continue')}
      />

      <ConfirmationPasswordModal
        isOpen={isOpenConfirmationPasswordModal}
        onClose={() => setIsOpenConfirmationPasswordModal(false)}
        onConfirm={() => {
          setIsOpenConfirmationPasswordModal(false);
          deleteAccount();
        }}
        isLoading={false}
        title={t('pages.accountdetail.button_confirm')}
        description={t('pages.accountdetail.password_description')}
        confirmLabel={t('pages.accountdetail.button_confirm')}
      />

      <CenteredSpinner isLoading={isLoading || deleteUserMutation.isLoading} />
    </PageContent>
  );
};
